import { throttle } from 'lodash';
import { WbTabArrows } from './WbTabArrows';

export class WbTabs {
  constructor() {
    this.breakpoint = 768;
    this.setKeys();
    this.setDirections();
    this.bindGlobalListeners();
    this.init();
  }

  init() {
    return new Promise(resolve => {
      this.generateArrays();
      this.bindListeners();

      resolve();
    });
  }

  bindGlobalListeners() {
    document.addEventListener('_page_loaded', () => this.init());
    window.addEventListener('resize', throttle(this.resize.bind(this), 300));
  }

  bindListeners() {
    const that = this;

    this.tablists?.forEach(tablist => {
      tablist.tabs?.forEach((tab, i) => {
        tab.addEventListener('click', that.clickEventListener.bind(that));
        tab.addEventListener('keydown', that.keydownEventListener.bind(that));
        tab.addEventListener('keyup', that.keyupEventListener.bind(that));
        tab.addEventListener('focus', that.focusEventHandler.bind(that));
        tab.index = i;
      });
    });
  }

  resetArrays() {
    this.tablists = [];
  }

  generateArrays() {
    this.resetArrays();
    this.tablists = document.querySelectorAll('.wb-tab-content__tablist');

    this.tablists?.forEach(tablist => {
      tablist.tabs = tablist.querySelectorAll('[role="tab"]');
      tablist.panels = tablist.closest('.wb-tab-content').querySelectorAll('[role="tabpanel"]');
      tablist.arrows = new WbTabArrows(tablist);
      this.activateTab(tablist.tabs[0]);
    });
  }

  resize() {
    this.setOrientation();
  }

  setOrientation() {
    const media = window.matchMedia(`(min-width: ${this.breakpoint}px)`);
    this.tablists?.forEach(tablist => {
      const orientation = tablist.getAttribute('data-orientation');
      if (media.matches) tablist.setAttribute('aria-orientation', orientation);
      else tablist.setAttribute('aria-orientation', 'horizontal');
    });
  }

  setKeys() {
    this.keys = {
      end: 'End',
      home: 'Home',
      left: 'ArrowLeft',
      up: 'ArrowUp',
      right: 'ArrowRight',
      down: 'ArrowDown',
      delete: 'Delete',
    };
  }

  setDirections() {
    this.directions = {
      ArrowLeft: -1,
      ArrowUp: -1,
      ArrowRight: 1,
      ArrowDown: 1,
    };
  }

  clickEventListener(event) {
    const tab = event.target;
    this.activateTab(tab, false);
  }

  keydownEventListener(event) {
    const { key } = event;
    const tablist = event.target.closest('[role="tablist"]');

    switch (key) {
      case this.keys.end:
        event.preventDefault();
        // Activate last tab
        this.activateTab(tablist.tabs[tablist.tabs.length - 1]);
        break;
      case this.keys.home:
        event.preventDefault();
        // Activate first tab
        this.activateTab(tablist.tabs[0]);
        break;
      // Up and down are in keydown
      // because we need to prevent page scroll
      case this.keys.up:
      case this.keys.down:
        this.determineOrientation(event);
        break;
      default:
    }
  }

  keyupEventListener(event) {
    const { key } = event;

    switch (key) {
      case this.keys.left:
      case this.keys.right:
        this.determineOrientation(event);
        break;
      default:
    }
  }

  determineOrientation(event) {
    const { key } = event;
    const tablist = event.target.closest('[role="tablist"]');
    const vertical = tablist.getAttribute('aria-orientation') === 'vertical';
    let proceed = false;

    if (vertical) {
      if (key === this.keys.up || key === this.keys.down) {
        event.preventDefault();
        proceed = true;
      }
    } else if (key === this.keys.left || key === this.keys.right) {
      proceed = true;
    }

    if (proceed) {
      this.switchTabOnArrowPress(event);
    }
  }

  switchTabOnArrowPress(event) {
    const { key, target } = event;
    const tablist = target.closest('[role="tablist"]');

    if (this.directions[key]) {
      if (target.index !== undefined) {
        if (tablist.tabs[target.index + this.directions[key]]) {
          tablist.tabs[target.index + this.directions[key]].focus();
        } else if (key === this.keys.left || key === this.keys.up) {
          tablist.tabs[tablist.tabs.length - 1].focus();
        } else if (key === this.keys.right || key === this.keys.down) {
          tablist.tabs[0].focus();
        }
      }
    }
  }

  activateTab(tab, setFocus = true) {
    const controls = tab.getAttribute('aria-controls');
    const tablist = tab.closest('[role="tablist"]');

    this.deactivateTabs(tablist);

    tab.setAttribute('tabindex', 0);
    tab.setAttribute('aria-selected', 'true');

    document.getElementById(controls).removeAttribute('hidden');
    tablist.arrows.resize();

    if (setFocus) {
      tab.focus();
    }
  }

  deactivateTabs(tablist) {
    tablist.tabs?.forEach(tab => {
      tab.setAttribute('tabindex', '-1');
      tab.setAttribute('aria-selected', 'false');
    });

    tablist.panels?.forEach(panel => {
      panel.setAttribute('hidden', 'hidden');
    });
  }

  focusEventHandler(event) {
    const that = this;
    const { target } = event;
    const tablist = target.closest('[role="tablist"]');

    setTimeout(that.checkTabFocus.bind(that), tablist.delay, target);
  }

  checkTabFocus(target) {
    const focused = document.activeElement;

    if (target === focused) {
      this.activateTab(target, false);
    }
  }
}
